import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useWindowSize } from '../utils/useWindowSize';
import { getHealingRequests, getUsers, getAuditLogs, getAuditActors, getPaymentAnalyticsSummary, getPaymentAnalyticsByHealer, getPackageStats } from '../utils/api';

// ── CSV download helper ───────────────────────────────────────────────────
function downloadCSV(filename, rows) {
  var csv = rows.map(function(row) {
    return row.map(function(cell) {
      var s = String(cell == null ? '' : cell);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) s = '"' + s.replace(/"/g, '""') + '"';
      return s;
    }).join(',');
  }).join('\n');
  var blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; }

// ── Date helpers ──────────────────────────────────────────────────────────
function getPresetRange(preset) {
  var now = new Date();
  var y = now.getFullYear();
  var m = now.getMonth();
  var d = now.getDate();
  if (preset === 'week') {
    var day = now.getDay();
    var mon = new Date(y, m, d - (day === 0 ? 6 : day - 1));
    return { from: mon.toISOString().split('T')[0], to: new Date().toISOString().split('T')[0] };
  }
  if (preset === 'month') return { from: new Date(y, m, 1).toISOString().split('T')[0], to: new Date().toISOString().split('T')[0] };
  if (preset === 'lastmonth') {
    var lm = new Date(y, m - 1, 1);
    var lme = new Date(y, m, 0);
    return { from: lm.toISOString().split('T')[0], to: lme.toISOString().split('T')[0] };
  }
  if (preset === 'quarter') {
    var qStart = new Date(y, Math.floor(m / 3) * 3, 1);
    return { from: qStart.toISOString().split('T')[0], to: new Date().toISOString().split('T')[0] };
  }
  if (preset === 'year') return { from: new Date(y, 0, 1).toISOString().split('T')[0], to: new Date().toISOString().split('T')[0] };
  return { from: '', to: '' };
}

const Finance = () => {
  const { isMobile } = useWindowSize();
  const [requests, setRequests] = useState([]);
  const [healers, setHealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterHealer, setFilterHealer] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activePreset, setActivePreset] = useState('all');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [payAnalytics,        setPayAnalytics]        = useState(null);
  const [payByHealer,         setPayByHealer]         = useState([]);
  const [payAnalyticsLoading, setPayAnalyticsLoading] = useState(false);
  const [pkgStats,            setPkgStats]            = useState(null);
  const [pkgStatsLoading,     setPkgStatsLoading]     = useState(false);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditActors, setAuditActors] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilterActor, setAuditFilterActor] = useState('');
  const [auditFilterCategory, setAuditFilterCategory] = useState('');
  const AUDIT_LIMIT = 50;

  useEffect(function() {
    (async function() {
      try {
        const res = await getAuditActors();
        setAuditActors(res.data.actors || []);
      } catch (err) {
        console.error('getAuditActors failed', err);
      }
    })();
  }, []);

  useEffect(function() {
    if (activeTab !== 'audit') return;
    setAuditLoading(true);
    var params = { page: auditPage, limit: AUDIT_LIMIT };
    if (auditFilterActor) params.performedBy = auditFilterActor;
    if (auditFilterCategory) params.category = auditFilterCategory;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    (async function() {
      try {
        const res = await getAuditLogs(params);
        setAuditLogs(res.data.logs || []);
        setAuditTotal(res.data.total || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setAuditLoading(false);
      }
    })();
  }, [activeTab, auditPage, auditFilterActor, auditFilterCategory, dateFrom, dateTo]);

  useEffect(function() {
    (async function() {
      try {
        const results = await Promise.allSettled([getHealingRequests(), getUsers()]);
        if (results[0].status === 'fulfilled') setRequests(results[0].value.data.data || []);
        if (results[1].status === 'fulfilled') {
          const allUsers = results[1].value.data.data || [];
          setHealers(allUsers.filter(function(u) { return u.role === 'healer'; }));
        }
      } catch (err) {
        console.error('Finance data fetch failed', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(function() {
    if (activeTab !== 'analytics') return;
    setPayAnalyticsLoading(true);
    var now = new Date();
    var month = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
    (async function() {
      try {
        var results = await Promise.allSettled([
          getPaymentAnalyticsSummary(month),
          getPaymentAnalyticsByHealer(month),
        ]);
        if (results[0].status === 'fulfilled') setPayAnalytics(results[0].value.data.data);
        if (results[1].status === 'fulfilled') setPayByHealer(results[1].value.data.data || []);
      } catch (err) {
        console.error('Payment analytics fetch failed', err);
      } finally {
        setPayAnalyticsLoading(false);
      }
    })();
  }, [activeTab]);

  useEffect(function() {
    if (activeTab !== 'promo') return;
    setPkgStatsLoading(true);
    var now = new Date();
    var month = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
    (async function() {
      try {
        var res = await getPackageStats(month);
        setPkgStats(res.data.data);
      } catch (err) {
        console.error('Package stats fetch failed', err);
      } finally {
        setPkgStatsLoading(false);
      }
    })();
  }, [activeTab]);

  var rangeLabel = (dateFrom || dateTo) ? ((dateFrom || 'Start') + '_to_' + (dateTo || 'Today')) : 'AllTime';

  var dlSummary = function() {
    setShowDownloadMenu(false);
    var rows = [
      ['Metric', 'Value'],
      ['Period', rangeLabel.replace(/_/g, ' ')],
      ['Total Revenue (₹)', totalRevenue],
      ['Net Revenue (₹)', netRevenue],
      ['Healer Total Entitlement (₹)', totalHealerEntitlement],
      ['Total Settled to Healers (₹)', totalSettledToHealers],
      ['Pending Payout (₹)', totalPendingPayout],
      ['Patient Invoices Generated', invoicesGenerated],
      ['Patient Invoices Pending', pendingInvoices],
      ['Healer Payments Raised', allHealerPayments.length],
      ['Healer Payments Pending', allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length],
    ];
    downloadCSV('PWC_Finance_Summary_' + rangeLabel + '.csv', rows);
  };

  var dlHealerSettlements = function() {
    setShowDownloadMenu(false);
    var rows = [['Healer', 'Email', 'Total Cases', 'Active', 'Completed', 'Sessions', 'Revenue (₹)', 'Entitlement (₹)', 'Settled (₹)', 'Pending (₹)', 'Settlement %']];
    healerStats.forEach(function(hs) {
      rows.push([hs.healer.firstName + ' ' + hs.healer.lastName, hs.healer.email, hs.caseCount, hs.activeCases, hs.completedCases, hs.totalSessions, hs.revenue, hs.entitlement, hs.settled, hs.pending, hs.pct + '%']);
    });
    downloadCSV('PWC_Healer_Settlements_' + rangeLabel + '.csv', rows);
  };

  var dlPatientInvoices = function() {
    setShowDownloadMenu(false);
    var rows = [['Patient', 'Condition', 'Amount (₹)', 'Amount Received', 'Invoice No.', 'Invoice Date', 'Case Status']];
    patientInvoices.forEach(function(inv) {
      rows.push([inv.patientName, inv.condition, inv.amountPaid, inv.amountReceived ? 'Yes' : 'No', inv.billNumber || 'Not Generated', fmtDate(inv.billGeneratedAt), inv.status]);
    });
    downloadCSV('PWC_Patient_Invoices_' + rangeLabel + '.csv', rows);
  };

  var dlPaymentLog = function() {
    setShowDownloadMenu(false);
    var rows = [['Payment No.', 'Healer', 'Patient', 'Condition', 'Sessions', 'Amount (₹)', 'Raised By', 'Raised On', 'Status', 'Settled On']];
    allHealerPayments.sort(function(a, b) { return new Date(b.raisedAt) - new Date(a.raisedAt); }).forEach(function(hp) {
      rows.push([hp.paymentNumber, hp.healerName, hp.patientName, hp.condition, hp.sessionsClaimed, hp.amountClaimed || 0, hp.raisedBy, fmtDate(hp.raisedAt), hp.status, fmtDate(hp.settledAt)]);
    });
    downloadCSV('PWC_Payment_Log_' + rangeLabel + '.csv', rows);
  };

  var applyPreset = function(preset) {
    setActivePreset(preset);
    if (preset === 'all') { setDateFrom(''); setDateTo(''); return; }
    var range = getPresetRange(preset);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  // ── Date filtering ────────────────────────────────────────────────────────
  var inRange = function(dateStr) {
    if (!dateFrom && !dateTo) return true;
    var d = new Date(dateStr);
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo) { var to = new Date(dateTo); to.setHours(23, 59, 59, 999); if (d > to) return false; }
    return true;
  };

  // Filter requests by requestedAt for case-based metrics
  var filteredRequests = requests.filter(function(r) { return inRange(r.requestedAt); });

  // ── Computed metrics ──────────────────────────────────────────────────────

  var totalRevenue = filteredRequests.reduce(function(s, r) {
    return s + (r.payment && r.payment.amountReceived ? (r.payment.amountPaid || 0) : 0);
  }, 0);

  var totalHealerEntitlement = filteredRequests.reduce(function(s, r) {
    return s + (r.payment && r.payment.healerShareAmount ? r.payment.healerShareAmount : 0);
  }, 0);

  var totalSettledToHealers = filteredRequests.reduce(function(s, r) {
    return s + (r.healerPayments || []).filter(function(p) { return p.status === 'settled'; }).reduce(function(ss, p) { return ss + (p.amountClaimed || 0); }, 0);
  }, 0);

  var totalPendingPayout = totalHealerEntitlement - totalSettledToHealers;
  var netRevenue = totalRevenue - totalSettledToHealers;

  var invoicesGenerated = filteredRequests.filter(function(r) { return r.payment && r.payment.billNumber; }).length;
  var pendingInvoices = filteredRequests.filter(function(r) { return r.payment && r.payment.amountReceived && r.payment.amountPaid > 0 && !r.payment.billNumber; }).length;

  var allHealerPayments = [];
  filteredRequests.forEach(function(r) {
    var patientName = r.patient ? (r.patient.firstName + ' ' + r.patient.lastName).trim() : (r.manualPatientName || 'Manual');
    var healerName = r.assignedHealer ? (r.assignedHealer.firstName + ' ' + r.assignedHealer.lastName).trim() : '—';
    (r.healerPayments || []).forEach(function(hp) {
      allHealerPayments.push({ ...hp, requestId: r._id, condition: r.condition, patientName, healerName });
    });
  });

  // ── Per-healer stats ──────────────────────────────────────────────────────
  var healerStats = healers.map(function(h) {
    var hRequests = filteredRequests.filter(function(r) { return r.assignedHealer && r.assignedHealer._id === h._id; });
    var revenue = hRequests.reduce(function(s, r) { return s + (r.payment && r.payment.amountReceived ? (r.payment.amountPaid || 0) : 0); }, 0);
    var entitlement = hRequests.reduce(function(s, r) { return s + (r.payment && r.payment.healerShareAmount ? r.payment.healerShareAmount : 0); }, 0);
    var settled = hRequests.reduce(function(s, r) {
      return s + (r.healerPayments || []).filter(function(p) { return p.status === 'settled'; }).reduce(function(ss, p) { return ss + (p.amountClaimed || 0); }, 0);
    }, 0);
    var pending = entitlement - settled;
    var pct = entitlement > 0 ? Math.round((settled / entitlement) * 100) : 0;
    var activeCases = hRequests.filter(function(r) { return r.status === 'assigned' || r.status === 'in_progress'; }).length;
    var completedCases = hRequests.filter(function(r) { return r.status === 'completed'; }).length;
    var totalSessions = hRequests.reduce(function(s, r) { return s + (r.totalSessionsCompleted || 0); }, 0);
    return { healer: h, revenue, entitlement, settled, pending, pct, activeCases, completedCases, totalSessions, caseCount: hRequests.length };
  }).filter(function(hs) { return hs.caseCount > 0; });

  // ── Patient invoices list ─────────────────────────────────────────────────
  var patientInvoices = filteredRequests.filter(function(r) { return r.payment && r.payment.amountPaid > 0; }).map(function(r) {
    return {
      _id: r._id,
      patientName: r.patient ? (r.patient.firstName + ' ' + r.patient.lastName).trim() : (r.manualPatientName || 'Manual'),
      condition: r.condition,
      amountPaid: r.payment.amountPaid || 0,
      amountReceived: r.payment.amountReceived,
      billNumber: r.payment.billNumber || null,
      billGeneratedAt: r.payment.billGeneratedAt || null,
      requestedAt: r.requestedAt,
      status: r.status
    };
  });

  // ── Filtered healer payments ──────────────────────────────────────────────
  var filteredPayments = allHealerPayments.filter(function(hp) {
    if (filterHealer !== 'all' && hp.healerName !== filterHealer) return false;
    if (filterStatus !== 'all' && hp.status !== filterStatus) return false;
    return true;
  });

  var pendingPaymentsTotal = allHealerPayments.filter(function(p) { return p.status === 'pending'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);
  var settledPaymentsTotal = allHealerPayments.filter(function(p) { return p.status === 'settled'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);

  // ── Styles ────────────────────────────────────────────────────────────────
  var mainPad = isMobile ? '56px 16px 16px' : '32px';
  var mainMargin = isMobile ? 0 : '260px';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" }}>
      <Sidebar />
      <div style={{ flex: 1, marginLeft: mainMargin, padding: mainPad, minWidth: 0 }}>

        {/* Header */}
        <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway', sans-serif", margin: '0 0 4px' }}>Financial Overview</h1>
            <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>Revenue, healer settlements, and invoice tracking</p>
          </div>
          {/* Download dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={function() { setShowDownloadMenu(!showDownloadMenu); }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: "'Raleway',sans-serif", fontSize: '13px', fontWeight: '700', letterSpacing: '0.3px', boxShadow: '0 2px 6px rgba(74,143,130,0.3)' }}>
              ↓ Download Report
            </button>
            {showDownloadMenu && (
              <>
                <div onClick={function() { setShowDownloadMenu(false); }} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: '220px', zIndex: 1000, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                  <div style={{ padding: '8px 14px 6px', fontSize: '10px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', borderBottom: '1px solid #f0f0f0' }}>
                    {rangeLabel.replace(/_/g, ' ')}
                  </div>
                  {[
                    { label: 'Summary Report', sub: 'KPI totals', fn: dlSummary },
                    { label: 'Healer Settlements', sub: 'Per-healer breakdown', fn: dlHealerSettlements },
                    { label: 'Patient Invoices', sub: 'All invoice records', fn: dlPatientInvoices },
                    { label: 'Payment Log', sub: 'All healer payments', fn: dlPaymentLog },
                  ].map(function(item) {
                    return (
                      <button key={item.label} onClick={item.fn} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                        onMouseEnter={function(e) { e.currentTarget.style.background = '#f9faf7'; }}
                        onMouseLeave={function(e) { e.currentTarget.style.background = 'none'; }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{item.label}</div>
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '1px' }}>{item.sub} — CSV</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Date Filter Bar */}
        <div style={{ background: '#fff', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          {/* Quick presets */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[
              { key: 'all', label: 'All Time' },
              { key: 'week', label: 'This Week' },
              { key: 'month', label: 'This Month' },
              { key: 'lastmonth', label: 'Last Month' },
              { key: 'quarter', label: 'This Quarter' },
              { key: 'year', label: 'This Year' },
            ].map(function(p) {
              return (
                <button key={p.key} onClick={function() { applyPreset(p.key); }} style={{ padding: '5px 12px', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway',sans-serif", letterSpacing: '0.3px', background: activePreset === p.key ? '#4a8f82' : '#f0f0f0', color: activePreset === p.key ? '#fff' : '#555', transition: 'all 0.2s' }}>
                  {p.label}
                </button>
              );
            })}
          </div>
          {/* Divider */}
          <div style={{ width: '1px', height: '24px', background: '#e0e0e0', flexShrink: 0 }} />
          {/* Custom range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', color: '#999', fontWeight: '600' }}>Custom:</span>
            <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); setActivePreset('custom'); }} style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', color: '#333' }} />
            <span style={{ fontSize: '12px', color: '#999' }}>to</span>
            <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); setActivePreset('custom'); }} style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', color: '#333' }} />
          </div>
          {/* Active indicator */}
          {(dateFrom || dateTo) && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#4a8f82', fontWeight: '600' }}>
                {dateFrom || 'Start'} → {dateTo || 'Today'} &nbsp;({filteredRequests.length} cases)
              </span>
              <button onClick={function() { applyPreset('all'); }} style={{ fontSize: '11px', padding: '3px 8px', background: '#f0f0f0', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#666' }}>Clear</button>
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#999', fontSize: '14px' }}>Loading financial data...</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: '16px', marginBottom: '28px' }}>
              <KPICard label="Total Revenue" value={'₹' + totalRevenue.toLocaleString('en-IN')} sub={filteredRequests.filter(function(r) { return r.payment && r.payment.amountReceived; }).length + ' cases paid'} color="#27ae60" />
              <KPICard label="Net Revenue" value={'₹' + netRevenue.toLocaleString('en-IN')} sub="After healer payouts" color="#2980b9" />
              <KPICard label="Paid to Healers" value={'₹' + totalSettledToHealers.toLocaleString('en-IN')} sub={'of ₹' + totalHealerEntitlement.toLocaleString('en-IN') + ' entitlement'} color="#4a8f82" />
              <KPICard label="Pending Payout" value={'₹' + totalPendingPayout.toLocaleString('en-IN')} sub={allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length + ' pending invoice(s)'} color={totalPendingPayout > 0 ? '#e74c3c' : '#27ae60'} />
            </div>

            {/* Secondary KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: '16px', marginBottom: '28px' }}>
              <MiniCard label="Patient Invoices Sent" value={invoicesGenerated} />
              <MiniCard label="Invoices Pending" value={pendingInvoices} alert={pendingInvoices > 0} />
              <MiniCard label="Healer Payments Raised" value={allHealerPayments.length} />
              <MiniCard label="Healer Payments Pending" value={allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length} alert={allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length > 0} />
            </div>

            {/* Revenue bar */}
            {totalRevenue > 0 && (
              <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', marginBottom: '28px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>Revenue Breakdown</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#555', width: '140px' }}>Healer Settled</div>
                  <div style={{ flex: 1, height: '10px', background: '#f0f0f0', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: (totalRevenue > 0 ? (totalSettledToHealers / totalRevenue) * 100 : 0) + '%', background: '#4a8f82', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                  </div>
                  <div style={{ fontSize: '12px', color: '#4a8f82', fontWeight: '700', width: '100px', textAlign: 'right' }}>₹{totalSettledToHealers.toLocaleString('en-IN')}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#555', width: '140px' }}>Healer Pending</div>
                  <div style={{ flex: 1, height: '10px', background: '#f0f0f0', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: (totalRevenue > 0 ? (totalPendingPayout / totalRevenue) * 100 : 0) + '%', background: '#e74c3c', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                  </div>
                  <div style={{ fontSize: '12px', color: '#e74c3c', fontWeight: '700', width: '100px', textAlign: 'right' }}>₹{totalPendingPayout.toLocaleString('en-IN')}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#555', width: '140px' }}>Net (Centre Retains)</div>
                  <div style={{ flex: 1, height: '10px', background: '#f0f0f0', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: (totalRevenue > 0 ? (netRevenue / totalRevenue) * 100 : 0) + '%', background: '#2980b9', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                  </div>
                  <div style={{ fontSize: '12px', color: '#2980b9', fontWeight: '700', width: '100px', textAlign: 'right' }}>₹{netRevenue.toLocaleString('en-IN')}</div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#fff', padding: '6px', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
              {[
                { key: 'overview', label: 'Healer Settlements' },
                { key: 'invoices', label: 'Patient Invoices (' + patientInvoices.length + ')' },
                { key: 'payments', label: 'Payment Log (' + allHealerPayments.length + ')' },
                { key: 'pending', label: 'Pending Actions' + (pendingInvoices + allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length > 0 ? ' (' + (pendingInvoices + allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length) + ')' : '') },
                { key: 'audit', label: 'Audit Log' },
                { key: 'analytics', label: 'Analytics' },
                { key: 'promo', label: 'Promo Performance' },
              ].map(function(tab) {
                return (
                  <button key={tab.key} onClick={function() { setActiveTab(tab.key); }} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '0.3px', whiteSpace: 'nowrap', background: activeTab === tab.key ? '#4a8f82' : 'transparent', color: activeTab === tab.key ? '#fff' : '#666', transition: 'all 0.2s' }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* ── HEALER SETTLEMENTS TAB ── */}
            {activeTab === 'overview' && (
              healerStats.length === 0 ? (
                <EmptyState text="No healer payment data yet." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {healerStats.sort(function(a, b) { return b.entitlement - a.entitlement; }).map(function(hs) {
                    return (
                      <div key={hs.healer._id} style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg,#4a8f82,#5a9a6e)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: "'Raleway',sans-serif", fontSize: '14px', fontWeight: '700', flexShrink: 0 }}>
                              {hs.healer.firstName ? hs.healer.firstName.charAt(0) : ''}{hs.healer.lastName ? hs.healer.lastName.charAt(0) : ''}
                            </div>
                            <div>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway',sans-serif" }}>{hs.healer.firstName} {hs.healer.lastName}</div>
                              <div style={{ fontSize: '12px', color: '#999' }}>{hs.healer.email}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                            <Stat label="Cases" value={hs.caseCount} />
                            <Stat label="Active" value={hs.activeCases} />
                            <Stat label="Done" value={hs.completedCases} />
                            <Stat label="Sessions" value={hs.totalSessions} />
                          </div>
                        </div>

                        {/* Settlement progress */}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: '12px', marginBottom: '14px' }}>
                          <FinCard label="Revenue Generated" value={'₹' + hs.revenue.toLocaleString('en-IN')} color="#27ae60" />
                          <FinCard label="Total Entitlement" value={'₹' + hs.entitlement.toLocaleString('en-IN')} color="#4a8f82" />
                          <FinCard label="Pending Payout" value={'₹' + hs.pending.toLocaleString('en-IN')} color={hs.pending > 0 ? '#e74c3c' : '#27ae60'} />
                        </div>

                        {hs.entitlement > 0 && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <div style={{ fontSize: '11px', color: '#999' }}>Settlement Progress</div>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: hs.pct === 100 ? '#27ae60' : '#4a8f82' }}>₹{hs.settled.toLocaleString('en-IN')} settled ({hs.pct}%)</div>
                            </div>
                            <div style={{ height: '8px', background: '#f0f0f0', borderRadius: '6px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: hs.pct + '%', background: hs.pct === 100 ? '#27ae60' : '#4a8f82', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* ── PATIENT INVOICES TAB ── */}
            {activeTab === 'invoices' && (
              patientInvoices.length === 0 ? (
                <EmptyState text="No patient payment records yet." />
              ) : (
                <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9faf7' }}>
                          {['Patient', 'Condition', 'Amount', 'Invoice No.', 'Invoice Date', 'Status'].map(function(h) {
                            return <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {patientInvoices.sort(function(a, b) { return new Date(b.requestedAt) - new Date(a.requestedAt); }).map(function(inv) {
                          return (
                            <tr key={inv._id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                              <td style={tdStyle}><strong>{inv.patientName}</strong></td>
                              <td style={{ ...tdStyle, color: '#666' }}>{inv.condition}</td>
                              <td style={tdStyle}>
                                <span style={{ color: inv.amountReceived ? '#27ae60' : '#d4ac0d', fontWeight: '700' }}>
                                  ₹{inv.amountPaid.toLocaleString('en-IN')}
                                </span>
                                {!inv.amountReceived && <span style={{ fontSize: '10px', color: '#d4ac0d', display: 'block' }}>Pending receipt</span>}
                              </td>
                              <td style={tdStyle}>
                                {inv.billNumber
                                  ? <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#2980b9' }}>{inv.billNumber}</span>
                                  : <span style={{ fontSize: '11px', color: '#e74c3c', fontWeight: '600' }}>Not generated</span>}
                              </td>
                              <td style={{ ...tdStyle, color: '#999', fontSize: '12px' }}>
                                {inv.billGeneratedAt ? new Date(inv.billGeneratedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                              </td>
                              <td style={tdStyle}>
                                <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', fontWeight: '700', background: inv.billNumber ? '#eafaf1' : '#fdf0f0', color: inv.billNumber ? '#27ae60' : '#e74c3c' }}>
                                  {inv.billNumber ? 'Invoiced' : 'Pending'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            )}

            {/* ── PAYMENT LOG TAB ── */}
            {activeTab === 'payments' && (
              <div>
                {/* Summary + Filters */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '13px', color: '#555' }}>Settled: <strong style={{ color: '#27ae60' }}>₹{settledPaymentsTotal.toLocaleString('en-IN')}</strong></div>
                    <div style={{ fontSize: '13px', color: '#555' }}>Pending: <strong style={{ color: '#e74c3c' }}>₹{pendingPaymentsTotal.toLocaleString('en-IN')}</strong></div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <select value={filterHealer} onChange={function(e) { setFilterHealer(e.target.value); }} style={selectStyle}>
                      <option value="all">All Healers</option>
                      {[...new Set(allHealerPayments.map(function(p) { return p.healerName; }))].map(function(n) {
                        return <option key={n} value={n}>{n}</option>;
                      })}
                    </select>
                    <select value={filterStatus} onChange={function(e) { setFilterStatus(e.target.value); }} style={selectStyle}>
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="settled">Settled</option>
                    </select>
                  </div>
                </div>

                {filteredPayments.length === 0 ? (
                  <EmptyState text="No healer payment entries yet." />
                ) : (
                  <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f9faf7' }}>
                            {['Payment No.', 'Healer', 'Patient / Case', 'Sessions', 'Amount', 'Raised By', 'Raised On', 'Status', 'Settled On'].map(function(h) {
                              return <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>;
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPayments.sort(function(a, b) { return new Date(b.raisedAt) - new Date(a.raisedAt); }).map(function(hp, idx) {
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5', background: hp.status === 'settled' ? '#fff' : '#fffdf5' }}>
                                <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#2980b9' }}>{hp.paymentNumber}</span></td>
                                <td style={tdStyle}><strong>{hp.healerName}</strong></td>
                                <td style={{ ...tdStyle, maxWidth: '160px' }}>
                                  <div style={{ fontWeight: '600', color: '#333', fontSize: '13px' }}>{hp.patientName}</div>
                                  <div style={{ color: '#999', fontSize: '11px' }}>{hp.condition}</div>
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'center' }}>{hp.sessionsClaimed}</td>
                                <td style={tdStyle}><strong style={{ color: '#1a1a2e' }}>₹{(hp.amountClaimed || 0).toLocaleString('en-IN')}</strong></td>
                                <td style={tdStyle}>
                                  <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: hp.raisedBy === 'admin' ? '#eaf4fb' : '#f9faf7', color: hp.raisedBy === 'admin' ? '#2980b9' : '#4a8f82', fontWeight: '600' }}>{hp.raisedBy}</span>
                                </td>
                                <td style={{ ...tdStyle, color: '#999', fontSize: '12px', whiteSpace: 'nowrap' }}>{new Date(hp.raisedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                <td style={tdStyle}>
                                  <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', fontWeight: '700', background: hp.status === 'settled' ? '#eafaf1' : '#fef9e7', color: hp.status === 'settled' ? '#27ae60' : '#d4ac0d' }}>
                                    {hp.status}
                                  </span>
                                </td>
                                <td style={{ ...tdStyle, color: '#999', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                  {hp.settledAt ? new Date(hp.settledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f9faf7', borderTop: '2px solid #e0e0e0' }}>
                            <td colSpan="4" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#666' }}>
                              {filteredPayments.length} entries
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>
                              ₹{filteredPayments.reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0).toLocaleString('en-IN')}
                            </td>
                            <td colSpan="4" />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── AUDIT LOG TAB ── */}
            {activeTab === 'audit' && (
              <div>
                {/* Filters */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
                  <select value={auditFilterCategory} onChange={function(e) { setAuditFilterCategory(e.target.value); setAuditPage(1); }} style={selectStyle}>
                    <option value="">All Categories</option>
                    <option value="healing_request">Healing Request</option>
                    <option value="finance">Finance</option>
                    <option value="user">User</option>
                    <option value="auth">Auth</option>
                  </select>
                  <select value={auditFilterActor} onChange={function(e) { setAuditFilterActor(e.target.value); setAuditPage(1); }} style={selectStyle}>
                    <option value="">All Users</option>
                    {auditActors.map(function(a) {
                      return <option key={a._id} value={a._id}>{a.name} ({a.role})</option>;
                    })}
                  </select>
                  <span style={{ fontSize: '12px', color: '#999', marginLeft: 'auto' }}>
                    {auditTotal} total entries
                  </span>
                </div>

                {auditLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999', fontSize: '14px' }}>Loading audit log...</div>
                ) : auditLogs.length === 0 ? (
                  <EmptyState text="No audit log entries yet. Actions like healer assignments, payments, and invoices will appear here." />
                ) : (
                  <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f9faf7' }}>
                            {['Time', 'Action', 'Performed By', 'Case / Target', 'Description'].map(function(h) {
                              return <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>;
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {auditLogs.map(function(log, idx) {
                            var catColors = {
                              healing_request: { bg: '#eaf4fb', color: '#2980b9' },
                              finance: { bg: '#eafaf1', color: '#27ae60' },
                              user: { bg: '#f5eef8', color: '#8e44ad' },
                              auth: { bg: '#fef9e7', color: '#d4ac0d' }
                            };
                            var c = catColors[log.category] || { bg: '#f0f0f0', color: '#666' };
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                <td style={{ ...tdStyle, color: '#999', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                  {new Date(log.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </td>
                                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                  <span style={{ fontSize: '10px', padding: '3px 7px', borderRadius: '4px', fontWeight: '700', background: c.bg, color: c.color, letterSpacing: '0.5px' }}>
                                    {log.action.replace(/_/g, ' ')}
                                  </span>
                                </td>
                                <td style={tdStyle}>
                                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{log.performedByName || '—'}</div>
                                  <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{log.performedByRole}</div>
                                </td>
                                <td style={{ ...tdStyle, maxWidth: '150px' }}>
                                  <div style={{ fontSize: '13px', color: '#333', fontWeight: '500' }}>{log.targetLabel || '—'}</div>
                                  {log.targetType && <div style={{ fontSize: '10px', color: '#bbb' }}>{log.targetType}</div>}
                                </td>
                                <td style={{ ...tdStyle, color: '#555', maxWidth: '280px' }}>
                                  <div style={{ fontSize: '12px', lineHeight: '1.5' }}>{log.description}</div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {auditTotal > AUDIT_LIMIT && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #f0f0f0', background: '#f9faf7' }}>
                        <span style={{ fontSize: '12px', color: '#999' }}>
                          Page {auditPage} of {Math.ceil(auditTotal / AUDIT_LIMIT)} — {auditTotal} entries
                        </span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={function() { setAuditPage(function(p) { return Math.max(1, p - 1); }); }} disabled={auditPage === 1}
                            style={{ padding: '5px 12px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: auditPage === 1 ? 'not-allowed' : 'pointer', fontSize: '12px', color: auditPage === 1 ? '#ccc' : '#333' }}>
                            ← Prev
                          </button>
                          <button onClick={function() { setAuditPage(function(p) { return Math.min(Math.ceil(auditTotal / AUDIT_LIMIT), p + 1); }); }} disabled={auditPage >= Math.ceil(auditTotal / AUDIT_LIMIT)}
                            style={{ padding: '5px 12px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: auditPage >= Math.ceil(auditTotal / AUDIT_LIMIT) ? 'not-allowed' : 'pointer', fontSize: '12px', color: auditPage >= Math.ceil(auditTotal / AUDIT_LIMIT) ? '#ccc' : '#333' }}>
                            Next →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── PENDING ACTIONS TAB ── */}
            {activeTab === 'pending' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* Invoices not generated */}
                <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway',sans-serif", marginBottom: '14px' }}>
                    Patient Invoices Not Generated
                    {pendingInvoices > 0 && <span style={{ marginLeft: '8px', background: '#e74c3c', color: '#fff', borderRadius: '10px', padding: '2px 8px', fontSize: '11px' }}>{pendingInvoices}</span>}
                  </div>
                  {patientInvoices.filter(function(inv) { return !inv.billNumber && inv.amountReceived; }).length === 0 ? (
                    <div style={{ color: '#27ae60', fontSize: '13px' }}>✓ All paid cases have invoices generated.</div>
                  ) : patientInvoices.filter(function(inv) { return !inv.billNumber && inv.amountReceived; }).map(function(inv) {
                    return (
                      <div key={inv._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{inv.patientName}</div>
                          <div style={{ fontSize: '12px', color: '#999' }}>{inv.condition} — ₹{inv.amountPaid.toLocaleString('en-IN')}</div>
                        </div>
                        <span style={{ fontSize: '11px', color: '#e74c3c', fontWeight: '700' }}>Invoice pending</span>
                      </div>
                    );
                  })}
                </div>

                {/* Healer invoices awaiting settlement */}
                <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway',sans-serif", marginBottom: '14px' }}>
                    Healer Invoices Awaiting Settlement
                    {allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length > 0 && (
                      <span style={{ marginLeft: '8px', background: '#d4ac0d', color: '#fff', borderRadius: '10px', padding: '2px 8px', fontSize: '11px' }}>
                        {allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length}
                      </span>
                    )}
                  </div>
                  {allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length === 0 ? (
                    <div style={{ color: '#27ae60', fontSize: '13px' }}>✓ No pending healer invoices.</div>
                  ) : allHealerPayments.filter(function(p) { return p.status === 'pending'; }).map(function(hp, idx) {
                    return (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{hp.healerName}</div>
                          <div style={{ fontSize: '12px', color: '#999' }}>{hp.paymentNumber} — {hp.sessionsClaimed} session(s) — {hp.patientName}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#d4ac0d' }}>₹{(hp.amountClaimed || 0).toLocaleString('en-IN')}</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>{new Date(hp.raisedAt).toLocaleDateString('en-IN')}</div>
                        </div>
                      </div>
                    );
                  })}
                  {allHealerPayments.filter(function(p) { return p.status === 'pending'; }).length > 0 && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#d4ac0d' }}>
                        Total: ₹{pendingPaymentsTotal.toLocaleString('en-IN')}
                      </div>
                    </div>
                  )}
                </div>

                {/* Cases with payment not received */}
                <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway',sans-serif", marginBottom: '14px' }}>
                    Active Cases — Payment Not Received
                    {filteredRequests.filter(function(r) { return (r.status === 'assigned' || r.status === 'in_progress') && (!r.payment || !r.payment.amountReceived); }).length > 0 && (
                      <span style={{ marginLeft: '8px', background: '#999', color: '#fff', borderRadius: '10px', padding: '2px 8px', fontSize: '11px' }}>
                        {filteredRequests.filter(function(r) { return (r.status === 'assigned' || r.status === 'in_progress') && (!r.payment || !r.payment.amountReceived); }).length}
                      </span>
                    )}
                  </div>
                  {filteredRequests.filter(function(r) { return (r.status === 'assigned' || r.status === 'in_progress') && (!r.payment || !r.payment.amountReceived); }).length === 0 ? (
                    <div style={{ color: '#27ae60', fontSize: '13px' }}>✓ All active cases have payment recorded.</div>
                  ) : filteredRequests.filter(function(r) { return (r.status === 'assigned' || r.status === 'in_progress') && (!r.payment || !r.payment.amountReceived); }).map(function(r) {
                    var pName = r.patient ? (r.patient.firstName + ' ' + r.patient.lastName).trim() : (r.manualPatientName || 'Manual');
                    var hName = r.assignedHealer ? (r.assignedHealer.firstName + ' ' + r.assignedHealer.lastName).trim() : 'Unassigned';
                    return (
                      <div key={r._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{pName}</div>
                          <div style={{ fontSize: '12px', color: '#999' }}>{r.condition} — Healer: {hName}</div>
                        </div>
                        <span style={{ fontSize: '11px', color: '#999', fontWeight: '600' }}>Payment pending</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── PAYMENT ANALYTICS SECTION ── */}
            {activeTab === 'analytics' && (
              <div style={{ marginBottom: '28px' }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway',sans-serif", marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #4a8f82' }}>
                  Payment Analytics — This Month
                </div>
                {payAnalyticsLoading ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>Loading payment analytics…</div>
                ) : payAnalytics ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: '16px' }}>
                      <KPICard label="Total Collected" value={'₹' + Math.round(payAnalytics.totalCollected / 100).toLocaleString('en-IN')} sub="This month (all sources)" color="#27ae60" />
                      <KPICard label="Conversion Rate" value={payAnalytics.conversionRate + '%'} sub="Paid / total requests" color="#2980b9" />
                      <KPICard label="Package Revenue" value={'₹' + Math.round(payAnalytics.packageRevenue / 100).toLocaleString('en-IN')} sub={payAnalytics.packageVsSingleSplit.packages + '% of total'} color="#8e44ad" />
                      <KPICard label="Pending Payments" value={payAnalytics.pendingPayments} sub="Awaiting collection" color={payAnalytics.pendingPayments > 0 ? '#e74c3c' : '#27ae60'} />
                    </div>

                    {payAnalytics.totalCollected > 0 && (
                      <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>Package vs Single Session Split</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <div style={{ fontSize: '12px', color: '#555', width: '140px' }}>Packages</div>
                          <div style={{ flex: 1, height: '10px', background: '#f0f0f0', borderRadius: '6px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: payAnalytics.packageVsSingleSplit.packages + '%', background: '#8e44ad', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                          </div>
                          <div style={{ fontSize: '12px', color: '#8e44ad', fontWeight: '700', width: '80px', textAlign: 'right' }}>{payAnalytics.packageVsSingleSplit.packages}%</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ fontSize: '12px', color: '#555', width: '140px' }}>Single Sessions</div>
                          <div style={{ flex: 1, height: '10px', background: '#f0f0f0', borderRadius: '6px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: payAnalytics.packageVsSingleSplit.single + '%', background: '#4a8f82', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                          </div>
                          <div style={{ fontSize: '12px', color: '#4a8f82', fontWeight: '700', width: '80px', textAlign: 'right' }}>{payAnalytics.packageVsSingleSplit.single}%</div>
                        </div>
                      </div>
                    )}

                    {payByHealer.length > 0 && (
                      <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                          Revenue by Healer — This Month
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f9faf7' }}>
                                {['Healer', 'Sessions', 'Revenue Collected'].map(function(h) {
                                  return <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>;
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {payByHealer.map(function(row) {
                                return (
                                  <tr key={String(row.healerId)} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                    <td style={tdStyle}><strong>{row.healerName || '—'}</strong></td>
                                    <td style={tdStyle}>{row.sessionCount}</td>
                                    <td style={tdStyle}><strong style={{ color: '#27ae60' }}>{'₹' + Math.round(row.totalCollected / 100).toLocaleString('en-IN')}</strong></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <EmptyState text="No payment analytics data available for this month." />
                )}
              </div>
            )}

            {/* ── ANALYTICS TAB (case & healer charts) ── */}
            {activeTab === 'analytics' && (function() {
              var allRequests = requests;
              var now = new Date();
              var months = [];
              for (var m = 5; m >= 0; m--) {
                var d = new Date(now.getFullYear(), now.getMonth() - m, 1);
                months.push({ label: d.toLocaleString('en-IN', { month: 'short', year: '2-digit' }), year: d.getFullYear(), month: d.getMonth(), count: 0, revenue: 0 });
              }
              allRequests.forEach(function(r) {
                var rd = new Date(r.requestedAt);
                var mo = months.find(function(x) { return x.year === rd.getFullYear() && x.month === rd.getMonth(); });
                if (mo) {
                  mo.count++;
                  if (r.payment && r.payment.amountReceived) mo.revenue += (r.payment.amountPaid || 0);
                }
              });
              var maxCount = Math.max(1, Math.max.apply(null, months.map(function(x) { return x.count; })));
              var maxRevenue = Math.max(1, Math.max.apply(null, months.map(function(x) { return x.revenue; })));

              var statusMap = { pending: 0, assigned: 0, in_progress: 0, completed: 0, cancelled: 0 };
              allRequests.forEach(function(r) { if (statusMap[r.status] !== undefined) statusMap[r.status]++; });
              var totalReqs = allRequests.length || 1;

              var healerMap = {};
              allRequests.forEach(function(r) {
                if (r.assignedHealer) {
                  var hid = r.assignedHealer._id;
                  if (!healerMap[hid]) healerMap[hid] = { name: r.assignedHealer.firstName + ' ' + r.assignedHealer.lastName, active: 0, total: 0 };
                  healerMap[hid].total++;
                  if (r.status === 'assigned' || r.status === 'in_progress') healerMap[hid].active++;
                }
              });
              var healerList = Object.values(healerMap).sort(function(a, b) { return b.total - a.total; }).slice(0, 8);
              var maxHealerTotal = Math.max(1, Math.max.apply(null, healerList.map(function(h) { return h.total; })));

              var statusColors2 = { pending: '#d4ac0d', assigned: '#2980b9', in_progress: '#27ae60', completed: '#4a8f82', cancelled: '#e74c3c' };
              var statusLabels = { pending: 'Pending', assigned: 'Assigned', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px' }}>
                    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '20px' }}>New Cases — Last 6 Months</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', height: '140px' }}>
                        {months.map(function(mo) {
                          var pct = Math.round((mo.count / maxCount) * 100);
                          return (
                            <div key={mo.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%', justifyContent: 'flex-end' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#4a8f82' }}>{mo.count || ''}</span>
                              <div style={{ width: '100%', background: '#4a8f82', borderRadius: '4px 4px 0 0', height: pct + '%', minHeight: mo.count > 0 ? '4px' : '0', transition: 'height 0.5s ease' }}></div>
                              <span style={{ fontSize: '10px', color: '#999', textAlign: 'center' }}>{mo.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '20px' }}>Case Status Breakdown</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {Object.entries(statusMap).map(function(entry) {
                          var pct = Math.round((entry[1] / totalReqs) * 100);
                          return (
                            <div key={entry[0]}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                                <span style={{ color: '#555', fontWeight: '600' }}>{statusLabels[entry[0]]}</span>
                                <span style={{ color: statusColors2[entry[0]], fontWeight: '700' }}>{entry[1]} ({pct}%)</span>
                              </div>
                              <div style={{ background: '#f0f0f0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                                <div style={{ width: pct + '%', height: '100%', background: statusColors2[entry[0]], borderRadius: '4px', transition: 'width 0.5s ease' }}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px' }}>
                    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '20px' }}>Revenue — Last 6 Months (₹)</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', height: '140px' }}>
                        {months.map(function(mo) {
                          var pct = Math.round((mo.revenue / maxRevenue) * 100);
                          return (
                            <div key={mo.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%', justifyContent: 'flex-end' }}>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: '#27ae60' }}>{mo.revenue > 0 ? '₹' + (mo.revenue / 1000).toFixed(1) + 'k' : ''}</span>
                              <div style={{ width: '100%', background: '#27ae60', borderRadius: '4px 4px 0 0', height: pct + '%', minHeight: mo.revenue > 0 ? '4px' : '0', transition: 'height 0.5s ease' }}></div>
                              <span style={{ fontSize: '10px', color: '#999', textAlign: 'center' }}>{mo.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '20px' }}>Healer Workload</div>
                      {healerList.length === 0 ? (
                        <div style={{ color: '#bbb', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No healer data yet</div>
                      ) : healerList.map(function(h) {
                        var pct = Math.round((h.total / maxHealerTotal) * 100);
                        return (
                          <div key={h.name} style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                              <span style={{ color: '#555', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{h.name}</span>
                              <span style={{ color: '#666', fontSize: '11px' }}>{h.active} active · {h.total} total</span>
                            </div>
                            <div style={{ background: '#f0f0f0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                              <div style={{ width: pct + '%', height: '100%', borderRadius: '4px', background: 'linear-gradient(to right, #4a8f82, #27ae60)', transition: 'width 0.5s ease' }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '16px' }}>
                    {[
                      { label: 'Total Cases', value: allRequests.length, color: '#4a8f82' },
                      { label: 'Total Sessions', value: allRequests.reduce(function(s, r) { return s + (r.totalSessionsCompleted || 0); }, 0), color: '#2980b9' },
                      { label: 'Total Revenue', value: '₹' + allRequests.reduce(function(s, r) { return s + (r.payment && r.payment.amountReceived ? (r.payment.amountPaid || 0) : 0); }, 0).toLocaleString('en-IN'), color: '#27ae60' },
                      { label: 'Completion Rate', value: allRequests.length > 0 ? Math.round(statusMap.completed / allRequests.length * 100) + '%' : '0%', color: '#8e44ad' }
                    ].map(function(stat) {
                      return (
                        <div key={stat.label} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '18px', textAlign: 'center' }}>
                          <div style={{ fontSize: '26px', fontWeight: '700', color: stat.color, fontFamily: "'Raleway', sans-serif" }}>{stat.value}</div>
                          <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: '4px', fontWeight: '600' }}>{stat.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ── PROMO PERFORMANCE TAB ── */}
            {activeTab === 'promo' && (
              pkgStatsLoading ? (
                <div style={{ padding: '48px', textAlign: 'center', color: '#888', fontSize: '14px' }}>Loading…</div>
              ) : !pkgStats ? (
                <div style={{ padding: '28px', textAlign: 'center', color: '#888', fontSize: '13px' }}>No promo data available for this month.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                  {/* KPI row */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '16px' }}>
                    {[
                      { label: 'Package Completion', value: pkgStats.packageCompletionRate + '%', color: '#27ae60' },
                      { label: 'Promos Used',        value: pkgStats.promoPerformance.totalPromosUsed, color: '#8e44ad' },
                      { label: 'Total Discount',     value: '₹' + Math.round(pkgStats.promoPerformance.totalDiscountGiven / 100).toLocaleString('en-IN'), color: '#e67e22' },
                      { label: 'Avg Sessions Used',  value: pkgStats.avgSessionsUsed, color: '#2980b9' },
                    ].map(function(stat) {
                      return (
                        <div key={stat.label} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '18px', textAlign: 'center' }}>
                          <div style={{ fontSize: '26px', fontWeight: '700', color: stat.color, fontFamily: "'Raleway', sans-serif" }}>{stat.value}</div>
                          <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: '4px', fontWeight: '600' }}>{stat.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Top promo */}
                  {pkgStats.promoPerformance.topPromo && (
                    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '14px' }}>Top Promo This Month</div>
                      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: '22px', fontWeight: '800', color: '#1a1a2e', fontFamily: "'Raleway', sans-serif", letterSpacing: '2px' }}>{pkgStats.promoPerformance.topPromo.code}</div>
                          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Promo code</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '22px', fontWeight: '800', color: '#8e44ad', fontFamily: "'Raleway', sans-serif" }}>{pkgStats.promoPerformance.topPromo.usedCount}</div>
                          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Times used</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '22px', fontWeight: '800', color: '#e67e22', fontFamily: "'Raleway', sans-serif" }}>{'₹' + Math.round(pkgStats.promoPerformance.topPromo.totalDiscount / 100).toLocaleString('en-IN')}</div>
                          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Total discount given</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Referral funnel */}
                  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '14px' }}>Referral Conversion Funnel</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr 1fr' : 'repeat(4, 1fr)', gap: '16px' }}>
                      {[
                        { label: 'Clicked',    value: pkgStats.referralConversion.clicked,    color: '#2980b9' },
                        { label: 'Registered', value: pkgStats.referralConversion.registered,  color: '#27ae60' },
                        { label: 'Approved',   value: pkgStats.referralConversion.approved,    color: '#4a8f82' },
                        { label: 'Conversion', value: pkgStats.referralConversion.conversionRate + '%', color: '#8e44ad' },
                      ].map(function(s) {
                        return (
                          <div key={s.label} style={{ textAlign: 'center', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                            <div style={{ fontSize: '24px', fontWeight: '800', color: s.color, fontFamily: "'Raleway', sans-serif" }}>{s.value}</div>
                            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px', fontWeight: '600' }}>{s.label}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: pkgStats.referralConversion.clicked || 1, height: '8px', background: '#2980b9', borderRadius: '4px 0 0 4px' }} />
                      <div style={{ flex: pkgStats.referralConversion.registered || 0, height: '8px', background: '#27ae60' }} />
                      <div style={{ flex: pkgStats.referralConversion.approved || 0, height: '8px', background: '#4a8f82', borderRadius: '0 4px 4px 0' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                      {[
                        { color: '#2980b9', label: 'Clicked' },
                        { color: '#27ae60', label: 'Registered' },
                        { color: '#4a8f82', label: 'Approved' },
                      ].map(function(l) {
                        return (
                          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: l.color }} />
                            <span style={{ fontSize: '12px', color: '#666' }}>{l.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Expiring packages */}
                  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '20px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '8px' }}>Expiring in Next 30 Days</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: pkgStats.expiringThisMonth > 0 ? '#e74c3c' : '#27ae60', fontFamily: "'Raleway', sans-serif" }}>{pkgStats.expiringThisMonth}</div>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Active packages expiring soon</div>
                  </div>

                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────

var tdStyle = { padding: '12px 16px', fontSize: '13px', color: '#333', verticalAlign: 'middle' };
var selectStyle = { padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', color: '#333', background: '#fff', cursor: 'pointer' };

function KPICard({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: '4px solid ' + color }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px', fontFamily: "'Raleway',sans-serif" }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway',sans-serif", marginBottom: '4px' }}>{value}</div>
      <div style={{ fontSize: '11px', color: '#999' }}>{sub}</div>
    </div>
  );
}

function MiniCard({ label, value, alert }) {
  return (
    <div style={{ background: '#fff', borderRadius: '10px', padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: '700', color: alert ? '#e74c3c' : '#1a1a2e', fontFamily: "'Raleway',sans-serif" }}>{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '18px', fontWeight: '700', color: '#4a8f82', fontFamily: "'Raleway',sans-serif" }}>{value}</div>
      <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</div>
    </div>
  );
}

function FinCard({ label, value, color }) {
  return (
    <div style={{ background: '#f9faf7', borderRadius: '8px', padding: '14px 16px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '18px', fontWeight: '700', color: color, fontFamily: "'Raleway',sans-serif" }}>{value}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ background: '#fff', borderRadius: '10px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', color: '#999', fontSize: '14px' }}>
      {text}
    </div>
  );
}

export default Finance;
